import { SagaIterator } from 'redux-saga';
import { getDeviationsByIds } from '@wix/da-shared-react/pkg/redux/deviations/selectors';
import { all, takeEvery, select, call, put } from 'redux-saga/effects';
import { BiEventType } from '@wix/da-bi/pkg/constants';
import { BIEventData, BIEventPayload } from '@wix/da-bi/pkg/types';
import { logPageView } from '@wix/da-shared-react/pkg/biLogger/redux/actions';
import { logBiEvent } from '@wix/da-shared-react/pkg/biLogger/redux/saga';
import isSSR from '@wix/da-shared-react/pkg/utils/isSSR';
import { getPagingMode } from '@wix/da-shared-react/pkg/publicSession/selectors';
import { APP_LOADED } from '../actions/app';
import { FETCH_SECTION_SUCCESS } from '../actions/sections';
import { getBiExtraData, getBiComponent } from '../selectors/biLogger';
import {
  biFetchMoreFolderItems,
  biClickProfileNavBar,
  biLogPostsFeedItems,
} from '../actions/bi';
import { selectors, withOffset } from '@wix/da-shared-react/pkg/Stream';
import { exitDuperbrowseDone } from '@wix/da-shared-react/pkg/Duperbrowse/redux/actionCreators';
import {
  getFullViewFolderIds,
  getCurrentlyViewedFolderId,
} from '../selectors/gallectionSection';
import { getSectionStreamId } from '../selectors/sections';
import {
  GallectionResourceType,
  GallectionType,
} from '@wix/da-shared-react/pkg/types/gallection';

export function* logSearchSubmit(searchQuery) {
  if (isSSR()) {
    return; // clientside only, as this event is also fired on SSR when search query is set from URL's query string
  }
  const event: BIEventData = {
    evid: BiEventType.GALLECTION_SEARCH_SUBMIT,
    search_text: searchQuery,
  };
  yield call(logBiEvent, event);
}

export function* pageViewHandler() {
  if (isSSR()) {
    return; // clientside only
  }
  const extraData = yield select(getBiExtraData);
  const component = yield select(getBiComponent);
  const event: BIEventPayload = {
    view: 'profile',
    component,
  };
  yield put(logPageView({ ...extraData, ...event }));

  const streamId = yield select(getSectionStreamId);
  const entityIds = yield select(selectors.getItems, streamId);
  const deviations = yield select(getDeviationsByIds, entityIds);
  yield put(biLogPostsFeedItems(deviations));
}

function* fetchSectionHandler(action) {
  if (isSSR()) {
    return; // clientside only
  }
  const extraData = yield select(getBiExtraData);
  const component = yield select(getBiComponent, action.section);
  const event: BIEventPayload = {
    view: 'profile',
    component,
  };
  yield put(logPageView({ ...extraData, ...event }));
}

function* infiniteScrollHandler(action) {
  const { streamId } = action.payload;
  const eventComponent = yield select(getBiComponent);
  const extraData = yield select(getBiExtraData);
  const biTotalItemsCount = yield select(selectors.getTotalItemsCount);
  const currentlyViewedFolderId = yield select(getCurrentlyViewedFolderId);
  const streamOffset = yield select(state =>
    withOffset.selectors.getNextOffset(state, streamId)
  );
  const streamParams = yield select(state =>
    selectors.getStreamParamsSafe(state, streamId)
  );
  const fullviewFolderIds = yield select(getFullViewFolderIds);

  if (streamOffset === 0 && fullviewFolderIds.length > 1) {
    const typeid =
      streamParams.gallectionType === GallectionType.COLLECTION
        ? GallectionResourceType.COLLECTION
        : GallectionResourceType.GALLERY;
    const nextFolderEvent: BIEventData = {
      evid: BiEventType.SCROLL_TO_LOAD_NEXT_FOLDER,
      component: eventComponent,
      view: 'profile',
      typeid,
      itemid: currentlyViewedFolderId,
      next_itemid: streamParams.folderId,
      next_typeid: typeid,
    };
    yield call(logBiEvent, nextFolderEvent);
  }

  const event: BIEventData = {
    evid: BiEventType.SCROLL_TO_LOAD_MORE,
    component: eventComponent,
    view: 'profile',
    itemid: extraData.itemid,
    typeid: extraData.typeid,
    offset: biTotalItemsCount,
  };

  yield call(logBiEvent, event);
}

function* biClickProfileNavBarHandler(action) {
  const { linkName } = action.payload;
  const eventComponent = yield select(getBiComponent);
  const extraData = yield select(getBiExtraData);
  yield call(logBiEvent, {
    evid: BiEventType.PROFILE_NAV_BAR,
    view: 'profile',
    component: eventComponent,
    link_name: linkName,
    itemid: extraData.itemid,
    typeid: extraData.typeid,
  });
}

export function* biLogPostsFeedItemsHandler(
  action: ReturnType<typeof biLogPostsFeedItems>
): SagaIterator {
  if (isSSR()) {
    return;
  }
  const {
    payload: { deviations },
  } = action;

  const streamId = yield select(getSectionStreamId);

  const pagingMode = yield select(getPagingMode);
  if (
    !streamId ||
    !deviations ||
    !deviations.length ||
    pagingMode !== 'scroll'
  ) {
    // POST_FEED_VIEW event should only fire for infinite scroll
    return;
  }
  const streamItems = yield select(selectors.getItems, streamId);
  const offset = streamItems.length - deviations.length;
  const metadata = JSON.stringify(
    deviations.map((item, index) => ({
      typeid: item.typeId,
      item: item.deviationId,
      rank: offset + index,
    }))
  );
  const { itemid, typeid } = yield select(getBiExtraData);
  const evid = BiEventType.POST_FEED_VIEW;

  yield call(logBiEvent, {
    evid,
    itemid,
    typeid,
    offset,
    metadata,
  });
}

export default function* biLogger() {
  yield all([
    takeEvery([APP_LOADED, exitDuperbrowseDone], pageViewHandler),
    takeEvery(FETCH_SECTION_SUCCESS, fetchSectionHandler),
    takeEvery(biFetchMoreFolderItems, infiniteScrollHandler),
    takeEvery(biClickProfileNavBar, biClickProfileNavBarHandler),
    takeEvery(biLogPostsFeedItems, biLogPostsFeedItemsHandler),
  ]);
}
